import React, { useEffect, useMemo } from "react";
import { Form, Input, Select, Space, Alert, Button, Tag } from "antd";
import { ReloadOutlined, PlusCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { FormInstance } from "antd";
import { DomainSelector } from "../DomainSelector";
import type { App, Template } from "../../types/api";
import { DeploymentStatus } from "../../types/enums";

const { Option } = Select;

export interface BasicInfoTabProps {
  form: FormInstance;
  editingApp: App | null;
  isEditMode: boolean;
  templates: Template[];
  appsQueryLoading: boolean;
  refreshAppsMutation: { mutate: () => void; isPending: boolean };
  existingCustomIds: Set<string>;
  allowedAppIds: string[];
}

const isTemplateWebSocket = (template?: Template | null): boolean => {
  return (template?.category || "").toLowerCase() === "websocket";
};

const deriveGreyType = (
  editingApp: App | null,
  templates: Template[],
  endpointMappings: any[] | undefined
): string => {
  if (editingApp?.greyType) return editingApp.greyType;
  const template = templates.find((tm) => tm.id === editingApp?.templateId);
  if (isTemplateWebSocket(template)) return "websocket";
  const hasWebSocket = (endpointMappings || []).some(
    (m: any) => m?.offerResponseType === "websocket"
  );
  return hasWebSocket ? "websocket" : "api";
};

export const BasicInfoTab: React.FC<BasicInfoTabProps> = ({
  form,
  editingApp,
  isEditMode,
  templates,
  appsQueryLoading,
  refreshAppsMutation,
  existingCustomIds,
  allowedAppIds,
}) => {
  const { t } = useTranslation();
  const endpointMappings = Form.useWatch("endpointMappings", form);
  const greyType = Form.useWatch("greyType", form);
  const templateId = Form.useWatch("templateId", form);

  // Ініціалізація greyType при редагуванні
  useEffect(() => {
    if (isEditMode && editingApp) {
      const initialGreyType = deriveGreyType(editingApp, templates, endpointMappings);
      form.setFieldsValue({ greyType: initialGreyType });
    } else if (!form.getFieldValue("greyType")) {
      form.setFieldsValue({ greyType: "api" });
    }
  }, [editingApp, isEditMode, templates, endpointMappings, form]);

  const filteredTemplates = useMemo(() => {
    if (!greyType) return templates;
    const isWs = greyType === "websocket";
    return templates.filter((tm) =>
      isWs ? isTemplateWebSocket(tm) : !isTemplateWebSocket(tm)
    );
  }, [templates, greyType]);

  // Автоматичний вибір/скидання шаблону при зміні Grey Type
  useEffect(() => {
    if (!greyType) return;
    const currentTemplate = templates.find((tm) => tm.id === templateId);
    const isCurrentWs = isTemplateWebSocket(currentTemplate);
    const isTargetWs = greyType === "websocket";

    if (isCurrentWs !== isTargetWs) {
      const firstMatch = filteredTemplates[0];
      form.setFieldsValue({ templateId: firstMatch ? firstMatch.id : undefined });
    }
  }, [greyType, filteredTemplates, templates, templateId, form]);

  const nameDisabled =
    editingApp?.deploymentStatus === DeploymentStatus.DEPLOYED ||
    editingApp?.deploymentStatus?.toUpperCase() === DeploymentStatus.DEPLOYED;
  const domainDisabled = isEditMode && nameDisabled;

  return (
    <div>
      <Form.Item
        label={t("apps.name")}
        name="name"
        rules={[{ required: true, message: t("apps.nameRequired") }]}
      >
        <Input disabled={nameDisabled} placeholder={t("apps.nameRequired")} />
      </Form.Item>
      <Form.Item
        label="Custom App ID"
        name="customId"
        tooltip="Оберіть ID зі списку апок, доступних у CRM для вашого акаунту."
        rules={[{ required: true, message: "Оберіть Custom App ID зі списку" }]}
      >
        {isEditMode && editingApp?.customId ? (
          <Input
            value={editingApp.customId}
            disabled
            placeholder="—"
          />
        ) : (
          <Space.Compact style={{ width: "100%" }}>
            <Select
              placeholder="Оберіть або введіть ID з CRM"
              showSearch
              allowClear
              style={{ flex: 1 }}
              loading={appsQueryLoading}
              filterOption={(input, option) =>
                (option?.value ?? "").toLowerCase().includes((input || "").toLowerCase())
              }
              options={allowedAppIds.map((id) => {
                const val = id.trim().toLowerCase();
                const exists = existingCustomIds.has(val);
                return {
                  value: val,
                  label: (
                    <Space size="small">
                      {exists ? (
                        <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      ) : (
                        <PlusCircleOutlined style={{ color: "#1890ff" }} />
                      )}
                      <span>{id}</span>
                      <Tag color={exists ? "success" : "blue"} style={{ marginLeft: 4 }}>
                        {exists ? "Вже в GM" : "Ще не створено"}
                      </Tag>
                    </Space>
                  ),
                };
              })}
              notFoundContent={
                appsQueryLoading
                  ? "Завантаження..."
                  : allowedAppIds.length === 0
                    ? "Немає доступних ID. Натисніть «Оновити»."
                    : "Нічого не знайдено"
              }
              value={form.getFieldValue("customId")}
              onChange={(v) => form.setFieldsValue({ customId: v ?? "" })}
            />
            <Button
              icon={<ReloadOutlined />}
              loading={refreshAppsMutation.isPending}
              onClick={() => refreshAppsMutation.mutate()}
              title="Оновити список апок та ID з CRM"
            >
              Оновити
            </Button>
          </Space.Compact>
        )}
      </Form.Item>

      <Form.Item
        label="Grey Type"
        name="greyType"
        rules={[{ required: true, message: "Оберіть Grey Type" }]}
      >
        <Select placeholder="Оберіть Grey Type">
          <Option value="api">GET API</Option>
          <Option value="websocket">Web-Socket</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={t("apps.template")}
        name="templateId"
        rules={[{ required: true, message: t("apps.templateRequired") }]}
      >
        <Select
          placeholder={t("apps.selectTemplate")}
          showSearch
          filterOption={(input, option) => {
            const template = filteredTemplates.find((tm) => tm.id === option?.value);
            return template?.name.toLowerCase().includes(input.toLowerCase()) ?? false;
          }}
        >
          {filteredTemplates.map((template) => {
            const isSelected = form.getFieldValue("templateId") === template.id;
            const isActive = template.active !== false;
            return (
              <Option key={template.id} value={template.id}>
                <Space>
                  <span style={{ color: isActive ? undefined : "#999" }}>
                    {template.name}
                    {template.version ? ` v${template.version}` : ""}
                  </span>
                  {template.category && (
                    <Tag>{template.category}</Tag>
                  )}
                  {!isActive && <span style={{ color: "#fa8c16" }}>Застарілий</span>}
                  {isSelected && <span style={{ color: "#1890ff" }}>Поточний</span>}
                </Space>
              </Option>
            );
          })}
        </Select>
      </Form.Item>

      <Form.Item
        label={t("apps.domain")}
        name="domain"
        rules={[{ required: true, message: t("apps.domainRequired") }]}
      >
        <DomainSelector
          disabled={domainDisabled}
          placeholder={t("apps.selectDomain")}
        />
      </Form.Item>

      <Alert
        message={t("apps.domainChangeWarning")}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Alert
        message={t("apps.createWarning")}
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
    </div>
  );
};
